import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, OnDestroy, OnInit, PLATFORM_ID, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IGolfSourceService } from '../../../services/IGolfCourse/igolf-source.service';
import { CommonModule, isPlatformBrowser, NgOptimizedImage } from '@angular/common';
import { finalize } from 'rxjs';
import { TranslatePipe } from '../../../pipes/translate.pipe';
import { ActivatedRoute, Router } from '@angular/router';
import { ToasterService } from '../../../shared/services/toaster.service';
import { SeoService } from '../../../services/seo.service';
import { SharedService } from '../../../shared/services/shared.service';
import { AnalyticsService, AnalyticsEvent } from '../../../services/analytics.service';
import { GolfCourse } from '../../../model/igolf.model';
import { customSettings, User } from '../../../model/user.model';
import { UserSettingService } from '../../../services/user-setting.service';
import { AnimationOptions, LottieComponent } from 'ngx-lottie';
import { FormsModule } from '@angular/forms';
import {
  DragScrollComponent,
  DragScrollItemDirective,
} from 'ngx-drag-scroll';
import { PlayGolfLandingComponent } from '../../play-golf-landing/play-golf-landing.component';
import { getCountryCodeByCountryId } from '../../../utils/country-const';
import { BackButton } from '../../../model/shared.model';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'app-on-course',
    imports: [CommonModule, TranslatePipe, LottieComponent, FormsModule, DragScrollComponent,
        DragScrollItemDirective, PlayGolfLandingComponent, NgOptimizedImage],
    templateUrl: './on-course.component.html',
    styleUrl: './on-course.component.scss',
    providers: [TranslatePipe]
})
export class OnCourseComponent implements OnInit, OnDestroy {
  courses = signal<GolfCourse[]>([]);
  noResult = signal(false);
  readonly pageSize = 15;
  private readonly pageNumber = signal(1);
  loading = signal(false);
  readonly loadingArray = new Array(4);
  /** Reactive so the view and the course-load effect update when it resolves. */
  isMobileDevice = signal(false);
  /** Guards the one-time nearby-course flow trigger. */
  private courseFlowStarted = false;
  private readonly userLatitude = signal(0);
  private readonly userLongitude = signal(0);
  readonly radius = 100;
  private watchId: number | null = null;
  showSetting = signal(false);
  readonly selectedCourse = signal<GolfCourse | null>(null);
  customizeObj = signal<customSettings>({
    general: '',
    onCourse: '',
    distance: 'yards',
    onCourseMode: 'social',
    onCourseAudio: 'on',
  });
  user = signal<User | null>(null);
  isSettingSaving = signal(false);
  isLocationLoading = signal(false);
  readonly isApple = signal(false);
  isLocationEnabled = signal(false);
  animationOptions: AnimationOptions = {
    path: '/assets/animations/Processing_Black.json',
  };
  /**
   * Login state from the cookie-aware session signal (the same source the
   * header trusts), NOT the localStorage access_token check — that token only
   * exists on localhost, so in production it was always false and forced the
   * desktop marketing page on mobile (CGA-2756).
   */
  readonly isLoggedIn = computed(() => this._sharedService.loggedIn());
  readonly courseImagePaths= ['assets/images/courses/course1.jpg', 'assets/images/courses/course2.jpg', 'assets/images/courses/course3.jpg', 'assets/images/courses/course4.jpg', 'assets/images/courses/course5.jpg','assets/images/courses/course1.jpg', 'assets/images/courses/course2.jpg', 'assets/images/courses/course3.jpg', 'assets/images/courses/course4.jpg', 'assets/images/courses/course5.jpg','assets/images/courses/course1.jpg', 'assets/images/courses/course2.jpg', 'assets/images/courses/course3.jpg', 'assets/images/courses/course4.jpg', 'assets/images/courses/course5.jpg'];
  private readonly prevButtonAction = signal(new BackButton());
  /** iGolfId from the `course` query param waiting for the course list to load. */
  private readonly pendingCourseParamId = signal<string | null>(null);
  private iGolfSourceService = inject(IGolfSourceService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private toastr = inject(ToasterService);
  private _sharedService = inject(SharedService);
  private _userSettingService = inject(UserSettingService);
  private platformId = inject<object>(PLATFORM_ID);
  private seoService = inject(SeoService);
  private _analytics = inject(AnalyticsService);
  private _destroyRef = inject(DestroyRef);

  constructor() {
    effect(() => {
      const buttonAction = this._sharedService.backButtonAction();
      if (buttonAction && buttonAction.action === 'onCourse') {
        this.selectedCourse.set(null);
        this.showSetting.set(false);
        setTimeout(() => {
          this._sharedService.backButton.set(this.prevButtonAction());
        }, 100);
      }
    });

    // Start the nearby-course flow only once we know the device is mobile AND
    // the session is authenticated. Both can resolve after init (login comes
    // from an async cookie check), so react to them rather than reading once in
    // ngOnInit. Runs at most once, in the browser only.
    effect(() => {
      const ready = this.isMobileDevice() && this.isLoggedIn();
      if (ready && !this.courseFlowStarted && isPlatformBrowser(this.platformId)) {
        this.courseFlowStarted = true;
        this.startMobileCourseFlow();
      }
    });
  }
  ngOnInit() {
    this.seoService.updateSeo();
    if (isPlatformBrowser(this.platformId)) {
      this.isMobileDevice.set(this.isMobile());
      this.getUserSetting();
    }
    // Open the course detail (Setup Round) panel when a `course` query param
    // (an iGolfId) is present — e.g. on reload or a shared/deep link.
    this.route.queryParamMap
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((params) => {
        this.pendingCourseParamId.set(params.get('course'));
        this.tryOpenCourseFromParam();
      });
  }

  /**
   * Resolves the pending `course` query param (iGolfId) against the loaded
   * course list and opens the Setup Round panel. No-op if the param is absent,
   * the panel is already open, or the matching course hasn't loaded yet.
   */
  private tryOpenCourseFromParam() {
    const iGolfId = this.pendingCourseParamId();
    if (!iGolfId || this.showSetting()) {
      return;
    }
    const match = this.courses().find((course) => this.getCourseIGolfId(course) === iGolfId);
    if (match) {
      this.selectedCourse.set(match);
      this.openCaddieSetting();
      this.pendingCourseParamId.set(null);
    }
  }

  /**
   * The iGolf identifier for a course. The igolf/courses endpoint returns it as
   * `id_course`; `iGolfId` is used as a fallback in case a source populates it.
   */
  private getCourseIGolfId(course: GolfCourse): string {
    return course.iGolfId || course.id_course || '';
  }

  /**
   * Requests the device location and loads nearby courses. Triggered by the
   * constructor effect once the device is mobile and the session is
   * authenticated, so it never runs before login state has resolved.
   */
  private startMobileCourseFlow(): void {
    this.isLocationLoading.set(true);
    navigator.geolocation.getCurrentPosition(
      (location) => {
        this.loading.set(true);
        this.isLocationEnabled.set(true);
        this.isLocationLoading.set(false);

        this.userLatitude.set(location.coords.latitude);
        this.userLongitude.set(location.coords.longitude);
        const leaveTime = localStorage.getItem('onCourseLeaveTime');
        const onCourseId = localStorage.getItem('onCourseId');
        if (leaveTime && onCourseId) {
          const currentTime = new Date().toISOString();
          const timeDiff = this.calculateTimeDifference(
            currentTime,
            leaveTime
          );
          if (timeDiff > 4) {
            localStorage.removeItem('onCourseLeaveTime');
            localStorage.removeItem('HoleNumber');
            localStorage.removeItem('onCourseId');
            localStorage.removeItem('OnCourseChatId');
            this.getAllCourses();
          } else {
            this.router.navigate(['in-play/' + onCourseId], {
              state: { hideExceptTopMenu: true },
            });
          }
        } else {
          this.getAllCourses();
        }
      },
      (error) => {
        const ua = navigator.userAgent;
        this.isApple.set(/iPhone|iPad|iPod/i.test(ua));
        this.isLocationLoading.set(false);
        this.isLocationEnabled.set(false);
      },
      {
        enableHighAccuracy: false,
        timeout: 60000,
        maximumAge: 10000, // Use cached location if within 30 seconds
      }
    );
  }

  /**
   * Detects a mobile context. UA sniffing alone is unreliable: iPadOS 13+
   * reports a desktop ("Macintosh") UA, and "Request desktop site" swaps in a
   * desktop UA on phones. So we also treat touch-capable Macs (iPad) and small
   * touch viewports (768px design breakpoint) as mobile.
   */
  isMobile(): boolean {
    if (typeof navigator === 'undefined') {
      return false;
    }
    const ua = (navigator.userAgent || navigator.vendor || '').toLowerCase();
    const touchPoints = navigator.maxTouchPoints || 0;

    const uaMobile =
      /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(ua);
    // iPadOS 13+ masquerades as desktop Safari but is touch-capable.
    const iPadOS = /macintosh/.test(ua) && touchPoints > 1;
    // Genuine small, touch-capable viewport (covers "request desktop site").
    const coarseSmall =
      typeof window !== 'undefined' &&
      !!window.matchMedia?.('(max-width: 768px)')?.matches &&
      touchPoints > 0;

    return uaMobile || iPadOS || coarseSmall;
  }
  getAllCourses() {
    this.loading.set(true);
    navigator.geolocation.getCurrentPosition(
      (location) => {
        this.userLatitude.set(location.coords.latitude);
        this.userLongitude.set(location.coords.longitude);
        // this.hasMoreItems = false;
        this.noResult.set(false);
        this.iGolfSourceService
          .getAllCourses(
            '',
            this.userLongitude(),
            this.userLatitude(),
            this.radius,
            this.pageNumber(),
            this.pageSize
          )
          .pipe(
            takeUntilDestroyed(this._destroyRef),
            finalize(() => {
              this.loading.set(false);
            })
          )
          .subscribe({
            next: (res) => {
              const mappedCourses = res.courseList.map(course => ({
                ...course,
                countryCode: getCountryCodeByCountryId(course.id_country) || '' 
              }));
              this.courses.update(arr => [...arr, ...mappedCourses]);
              if (this.courses().length == 0) {
                this.noResult.set(true);
              }
              // A `course` query param may have arrived before the list loaded.
              this.tryOpenCourseFromParam();
              // if (res.courseList.length == this.pageSize) {
              //   this.hasMoreItems = true;
              // }
            },
          });
      },
      (error) => {
        this.loading.set(false);
        const ua = navigator.userAgent;
        const isiOS = /iPhone|iPad|iPod/i.test(ua);
        const isMobile = /Android|iPhone|iPad|iPod/i.test(ua);
        this.toastr.error(
          'Error fetching location:' + error.message,
          'Location Error'
        );
      },
      {
        enableHighAccuracy: false,
        timeout: 60000,
        maximumAge: 10000, // Use cached location if within 30 seconds
      }
    );
  }
  selectCourse(selectedCourse: GolfCourse) {
    this.selectedCourse.set(selectedCourse);
  }

  setCustomize(patch: Partial<customSettings>) {
    this.customizeObj.update(obj => ({ ...obj, ...patch }));
  }

  navigateToselectedId() {
    this.isSettingSaving.set(true);
    this._userSettingService
      .customize(this.customizeObj())
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        finalize(() => {
          this.isSettingSaving.set(false);
        })
      )
      .subscribe({
        next: (res) => {
          this.user.update(u => u ? { ...u, customSettings: this.customizeObj() } : u);
          this._sharedService.setUser(this.user()!);
          this._analytics.track(AnalyticsEvent.PLAY_GOLF_COURSE_STARTED, {
            course_id: this.selectedCourse()?.id_course,
            course_name: this.selectedCourse()?.courseName,
          });
          this.router.navigate(
            ['in-play/' + this.selectedCourse()?.id_course],
            {
              state: { hideExceptTopMenu: true },
            }
          );
        },
      });
  }
  showCaddieSetting(course: GolfCourse) {
    this.selectedCourse.set(course);
    const iGolfId = this.getCourseIGolfId(course);
    if (iGolfId) {
      // Course has an iGolf id -> go to the play-golf course detail view,
      // reflecting the selected course in the URL so it can be reloaded/shared.
      this.router.navigate(['play-golf'], {
        queryParams: { course: iGolfId },
      });
      this.openCaddieSetting();
    } else {
      // No iGolf id -> fall back to the play-golf listing page.
      this.router.navigate(['play-golf']);
    }
  }

  /** Opens the Setup Round panel and wires up the back-button action. */
  private openCaddieSetting() {
    this.showSetting.set(true);
    this.prevButtonAction.set(this._sharedService.backButton());
    this._sharedService.backButton.set({
      show: true,
      url: '/',
      action: 'onCourse',
      text: ''
    });
  }
  loadMoreCourses() {
    this.pageNumber.update((page) => page + 1);
    this.getAllCourses();
  }
  calculateTimeDifference(currentTime: string, leaveTime: string): number {
    // Convert both times to Date objects
    const current = new Date(currentTime);
    const leave = new Date(leaveTime);

    // Get the difference in milliseconds
    const diffInMs = current.getTime() - leave.getTime();

    // Convert milliseconds to hours
    const diffInHours = diffInMs / (1000 * 60 * 60);

    return diffInHours;
  }
  getUserSetting() {
    this._sharedService.user$
    .pipe(takeUntilDestroyed(this._destroyRef))
    .subscribe({
      next: (res) => {
        this.user.set(res);
        if (res.customSettings) {
          const settings = { ...res.customSettings };
          if (!settings.onCourseAudio) {
            settings.onCourseAudio = 'on';
          }
          if (!settings.onCourseMode) {
            settings.onCourseMode = 'social';
          }
          if (!settings.distance) {
            settings.distance = 'yards';
          }
          this.customizeObj.set(settings);
        }
      },
    });
  }
  getGreeting(): string {
    const hour = new Date().getHours();

    if (hour >= 5 && hour < 12) {
      return 'Good Morning';
    } else if (hour >= 12 && hour < 17) {
      return 'Good Afternoon';
    } else if (hour >= 17 && hour < 21) {
      return 'Good Evening';
    } else {
      return 'Good Night';
    }
}
readonly firstName = computed(() => this.user()?.name?.split(' ')[0] || '');
  ngOnDestroy() {
    if (this.watchId !== null) {
      navigator.geolocation.clearWatch(this.watchId); // Stop geolocation tracking
    }
  }
}
